import { z } from 'zod';
import { IdSchema } from './common.js';

/**
 * Dialler campaigns (docs/36 D1b, 0127): the rules every part of the platform has to agree on --
 * when a person may lawfully be called, and what happens to a lead after each call.
 */

// =================================================================================== legal hours

/**
 * South Africa's public holidays for a year (Public Holidays Act 36 of 1994), with the Act's rule
 * that a holiday on a Sunday moves to the Monday. Good Friday and Family Day follow Easter.
 * Returned as YYYY-MM-DD.
 */
export function saPublicHolidays(year: number): string[] {
  const d = (m: number, day: number) => new Date(Date.UTC(year, m - 1, day));
  // Easter Sunday, Anonymous Gregorian algorithm.
  const a = year % 19, b = Math.floor(year / 100), c = year % 100;
  const dd = Math.floor(b / 4), e = b % 4, f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - dd - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  const easter = d(month, day);
  const plus = (base: Date, n: number) => new Date(base.getTime() + n * 86_400_000);

  const fixed = [d(1, 1), d(3, 21), d(4, 27), d(5, 1), d(6, 16), d(8, 9), d(9, 24), d(12, 16), d(12, 25), d(12, 26)];
  const out = new Set<string>();
  for (const x of fixed) {
    out.add(x.toISOString().slice(0, 10));
    if (x.getUTCDay() === 0) out.add(plus(x, 1).toISOString().slice(0, 10));
  }
  out.add(plus(easter, -2).toISOString().slice(0, 10)); // Good Friday
  out.add(plus(easter, 1).toISOString().slice(0, 10)); // Family Day
  return [...out].sort();
}

/**
 * The hard floor on when a campaign may call (docs/36 §9, CPA regulation 4 on direct marketing):
 * Monday to Friday 08:00-20:00, Saturday 09:00-13:00, never on a Sunday or a public holiday. No
 * campaign setting can widen it. In the account's time zone.
 */
export const LEGAL_HOURS: Record<number, [string, string] | null> = {
  1: ['08:00', '20:00'], 2: ['08:00', '20:00'], 3: ['08:00', '20:00'], 4: ['08:00', '20:00'], 5: ['08:00', '20:00'],
  6: ['09:00', '13:00'], 7: null,
};

export interface LocalNow { date: string; isoDay: number; time: string }

/** The account's date, ISO weekday (1 Monday) and HH:MM, for an instant. */
export function localNow(at: Date, timeZone: string): LocalNow {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    weekday: 'short', hourCycle: 'h23',
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  const isoDay = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].indexOf(get('weekday')) + 1;
  return { date: `${get('year')}-${get('month')}-${get('day')}`, isoDay, time: `${get('hour')}:${get('minute')}` };
}

export interface CallWindow { window_start: string; window_end: string; window_days: number[] }

/**
 * May this campaign call now? Why not, in words, when it may not. The campaign's own window is
 * applied inside the legal one; an account's own holidays (closed days) are passed in too.
 */
export function mayCallNow(
  at: Date, timeZone: string, w: CallWindow, extraClosedDates: readonly string[] = [],
): { ok: true } | { ok: false; why: string } {
  const now = localNow(at, timeZone);
  const legal = LEGAL_HOURS[now.isoDay];
  if (!legal) return { ok: false, why: 'Sundays are closed to campaigns by law.' };
  if (saPublicHolidays(Number(now.date.slice(0, 4))).includes(now.date)) {
    return { ok: false, why: 'Public holidays are closed to campaigns by law.' };
  }
  if (extraClosedDates.includes(now.date)) return { ok: false, why: 'The account is closed today.' };
  if (now.time < legal[0] || now.time >= legal[1]) {
    return { ok: false, why: `Campaigns may call from ${legal[0]} to ${legal[1]} today.` };
  }
  if (!w.window_days.includes(now.isoDay)) return { ok: false, why: 'The campaign does not call today.' };
  const start = w.window_start.slice(0, 5), end = w.window_end.slice(0, 5);
  if (now.time < start || now.time >= end) return { ok: false, why: `The campaign calls from ${start} to ${end}.` };
  return { ok: true };
}

// =================================================================================== after a call

export const CALL_OUTCOMES = [
  'answered', 'no_answer', 'busy', 'failed', 'machine', 'abandoned', 'cancelled',
  'optout', 'transferred', 'message', 'no_key',
] as const;
export type CallOutcome = (typeof CALL_OUTCOMES)[number];

export type RecycleRules = Record<string, { after_min: number; max: number }>;

export const DEFAULT_RECYCLE: RecycleRules = {
  busy: { after_min: 20, max: 3 },
  no_answer: { after_min: 180, max: 4 },
  machine: { after_min: 360, max: 3 },
  failed: { after_min: 5, max: 2 },
};

/**
 * What becomes of a lead after one call (docs/36 §5.3). The agent's disposition, when there is
 * one, decides over the call's outcome: a call that was answered by the wrong person is
 * "wrong_number", not "answered". A key in the rules means "try again after this long, at most this
 * many times"; anything else ends the lead in this campaign. So does the campaign's total cap.
 */
export function afterCall(input: {
  outcome: CallOutcome;
  disposition: string | null;
  attempts: number;
  counts: Record<string, number>;
  maxAttempts: number;
  rules: RecycleRules;
  now: Date;
}): { state: 'dialable' | 'done' | 'suppressed'; nextEligibleAt: Date; counts: Record<string, number>; reason: string } {
  const key = input.disposition ?? input.outcome;
  const counts = { ...input.counts, [key]: (input.counts[key] ?? 0) + 1 };
  if (input.outcome === 'optout') return { state: 'suppressed', nextEligibleAt: input.now, counts, reason: 'opted out' };
  if (input.outcome === 'cancelled') {
    // Skipped by the agent before dialling: back in the pool, no attempt spent.
    return { state: 'dialable', nextEligibleAt: input.now, counts: input.counts, reason: 'skipped' };
  }
  if (input.attempts >= input.maxAttempts) return { state: 'done', nextEligibleAt: input.now, counts, reason: 'attempt cap' };
  const rule = input.rules[key];
  if (!rule) return { state: 'done', nextEligibleAt: input.now, counts, reason: key };
  if (counts[key]! > rule.max) return { state: 'done', nextEligibleAt: input.now, counts, reason: `${key} ${rule.max} times` };
  return { state: 'dialable', nextEligibleAt: new Date(input.now.getTime() + rule.after_min * 60_000), counts, reason: key };
}

// =================================================================================== the API

const Time = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/, 'HH:MM');
const Destination = z.object({ type: z.enum(['queue', 'ring_group', 'extension', 'ivr']), id: IdSchema });

export const CampaignUpsertSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(500).nullable().optional(),
  mode: z.enum(['preview', 'auto']),
  window_start: Time.default('08:00'),
  window_end: Time.default('20:00'),
  window_days: z.array(z.number().int().min(1).max(7)).min(1).max(7).default([1, 2, 3, 4, 5, 6]),
  lead_order: z.enum(['oldest', 'newest', 'random']).default('oldest'),
  caller_id: z.string().regex(/^[1-9][0-9]{7,14}$/).nullable().optional(),
  max_attempts: z.number().int().min(1).max(50).default(8),
  recycle: z.record(z.string().regex(/^[a-z][a-z0-9_]{1,30}$/), z.object({
    after_min: z.number().int().min(1).max(60 * 24 * 30),
    max: z.number().int().min(1).max(20),
  })).default(DEFAULT_RECYCLE),
  sticky_agent_days: z.number().int().min(1).max(365).nullable().optional(),
  list_ids: z.array(IdSchema).max(50).default([]),
  agent_extension_ids: z.array(IdSchema).max(500).default([]),
  // auto
  billing_extension_id: IdSchema.nullable().optional(),
  message_media_id: IdSchema.nullable().optional(),
  transfer_destination: Destination.nullable().optional(),
  transfer_key: z.string().regex(/^[0-9*#]$/).default('1'),
  optout_key: z.string().regex(/^[0-9*#]$/).default('9'),
  max_lines: z.number().int().min(1).max(200).default(5),
  amd_mode: z.enum(['off', 'own', 'own_then_ai', 'ai']).default('off'),
  on_machine: z.enum(['hangup', 'message']).default('hangup'),
}).strict().superRefine((c, issue) => {
  if (c.window_end <= c.window_start) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['window_end'], message: 'Ends before it starts.' });
  if (c.transfer_key === c.optout_key) issue.addIssue({ code: z.ZodIssueCode.custom, path: ['optout_key'], message: 'Not the same key as transfer.' });
});
export type CampaignUpsert = z.infer<typeof CampaignUpsertSchema>;

export interface Campaign {
  id: string; name: string; description: string | null;
  mode: 'preview' | 'auto' | 'progressive' | 'power' | 'predictive';
  status: 'draft' | 'running' | 'paused' | 'finished';
  window_start: string; window_end: string; window_days: number[];
  lead_order: 'oldest' | 'newest' | 'random';
  caller_id: string | null; max_attempts: number; recycle: RecycleRules; sticky_agent_days: number | null;
  billing_extension_id: string | null; message_media_id: string | null;
  transfer_destination: { type: string; id: string } | null;
  transfer_key: string; optout_key: string; max_lines: number;
  amd_mode: 'off' | 'own' | 'own_then_ai' | 'ai'; on_machine: 'hangup' | 'message';
  list_ids: string[]; agent_extension_ids: string[];
  counts: { dialable: number; due_now: number; leased: number; done: number; suppressed: number; total: number };
  created_at: string; started_at: string | null;
}

/** What an agent sees when a preview campaign gives them a lead. */
export interface PreviewLead {
  campaign_record_id: string;
  attempt_id: string;
  campaign: { id: string; name: string };
  phone: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  fields: Array<{ key: string; label: string; value: string | number | boolean | null }>;
  attempts: number;
  why: 'callback' | 'retry' | 'fresh' | 'yours';
  callback_note: string | null;
  history: Array<{ at: string; outcome: string | null; disposition: string | null; note: string | null; agent: string | null }>;
}

export const PreviewResultSchema = z.object({
  outcome: z.enum(CALL_OUTCOMES),
  disposition: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/).nullable().optional(),
  note: z.string().trim().max(1000).nullable().optional(),
  callback_at: z.string().datetime({ offset: true }).nullable().optional(),
  callback_mine: z.boolean().default(true),
  do_not_call: z.boolean().default(false),
}).strict();
